// Search: pages and records that match, updated as you type. The top bar's box opens it with what was typed (Enter),
// and on a phone it's where the search button goes.
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CommandBar } from "../Shell";
import { Icon } from "../icons";
import { bcUrl } from "../config";
import { findPages } from "../findPages";
import { openPage, useRecordSearch } from "../TopSearch";
import { useAccess } from "../security/store";
import { RecordGrid, recordHref } from "../ui";

export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const access = useAccess();
  const [q, setQ] = useState(params.get("q") ?? "");
  // The address follows the box, so Back and a shared link come back to the same search.
  useEffect(() => { setQ(params.get("q") ?? ""); }, [params]);
  const type = (v: string) => { setQ(v); setParams(v.trim() ? { q: v } : {}, { replace: true }); };
  const t = q.trim();
  const pages = findPages(t, access, 12);
  const records = useRecordSearch(t, 150);
  // Twenty at a time, so the best matches stay in view; the rest are one click away.
  const [more, setMore] = useState(false);
  useEffect(() => setMore(false), [t]);
  const shown = records && !more ? records.slice(0, 20) : records;
  const status = !t ? "" : records === null ? "Searching…" : `${pages.length} page${pages.length === 1 ? "" : "s"} · ${records.length}${records.length >= 50 ? "+" : ""} record${records.length === 1 ? "" : "s"}`;
  return (
    <>
      <CommandBar status={status} />
      <div className="page">
        <div className="page-title"><h1>Search</h1></div>
        <form className="search-q" role="search" onSubmit={(e) => {
          // Enter opens the one obvious match: an exact record number, or the first page when no record matches.
          e.preventDefault();
          const rec = records?.find((r) => r.number.toLowerCase() === t.toLowerCase()) ?? (records?.length === 1 ? records[0] : undefined);
          if (rec) nav(recordHref(rec.type, rec.number)); else if (pages[0] && !records?.length) openPage(pages[0], nav);
        }}>
          <Icon name="search" size={16} />
          <input autoFocus aria-label="Search for a page or record" placeholder="Search for a page or record: a name, a number like ISS-00002, a lot or an item" value={q} onChange={(e) => type(e.target.value)} />
        </form>
        {!t ? (
          <div className="card empty">Type a page name (Issues, Work orders), a record number or title, or a Business Central lot, item or vendor.</div>
        ) : (
          <>
            {pages.length > 0 && (
              <section className="card search-pages" aria-label="Pages">
                <h2>Pages</h2>
                <ul>
                  {pages.map((p) => (
                    <li key={p.key}>
                      {p.to ? <Link to={p.to}>{p.name}</Link> : <a href={bcUrl(p.bc!)} target="_blank" rel="noopener noreferrer" title={`${p.name} opens in Business Central`}>{p.name}<span className="ts-ext"><Icon name="ext" size={11} /></span></a>}
                      <span className="muted">{p.where}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            <section className="card" aria-label="Records">
              {records?.length === 0 && pages.length > 0 ? (
                <div className="empty">No record has “{t}” in its number or title. {pages[0].to ? <>To see them all, open <Link to={pages[0].to}>{pages[0].name}</Link>.</> : null}</div>
              ) : <>
                <RecordGrid rows={shown} showType brief empty={`Nothing matches “${t}”. Try a record number, part of a title, a lot or an item.`} />
                {records && shown && records.length > shown.length && <div className="search-more"><button className="btn" onClick={() => setMore(true)}>Show all {records.length}{records.length >= 50 ? "+" : ""} records</button></div>}
              </>}
              {records?.length === 0 && !pages.length && <div className="empty" style={{ paddingTop: 0 }}>Can't find it? <Link to="/new">Raise an issue</Link> or go to <Link to="/">Courage home</Link>.</div>}
            </section>
          </>
        )}
      </div>
    </>
  );
}
